import { LocalizedTextIds } from '../config/localizedTextIds.js';
import {
	EditItemType,
	DynamicChoiceKey,
	UpdateConversion,
} from '../entity/company-update.interface.js';
import { ModelBase } from './_model.base.js';
import { Contact, CoOwnerContact } from './contact.js';
import { Deal } from './deal.js';
import {
	Metadata,
	Updateable,
	BlueKnight,
	getBlueKnightFieldsRaw,
	getUpdateableFieldsRaw,
	getMetadataRaw,
	UpdateOptions,
	getUpdateOptionsRaw,
} from './decorators.js';
import { Funding } from './funding.js';
import { LimitedDealOpportunity } from './limitedDealOpportunity.js';
import { Opportunity } from './opportunity.js';
import { Location } from './location.js';
import { Asset } from './asset.js';

export type CompanyKey = keyof Company | 'contactName' | 'companyContactTitle';

/**
 * @summary Note that a subset of fields are removed server-side based on a user's role, via stripSensitiveFields
 * and other fields may be removed based on feature flags.
 * @description This approach replaced a PublicCompany -> Company inheritance hierarchy that broke down when v5 introduced
 * multiple role profiles that had access to various subsets of Company properties.
 */
export class Company extends ModelBase {
	@Metadata({ indexable: true })
	public id: string;
	/** The date upon which a Company is considered to have engaged with JLABS.
	 * The basis of a "New Company" determination, and could be a month or so in the future.
	 */
	@Updateable({
		updateConversion: UpdateConversion.DateString,
	})
	@Metadata({ indexable: true })
	public commencementDate: Date;
	@Updateable({
		updateConversion: UpdateConversion.DateString,
	})
	@Metadata({ indexable: true })
	public graduationDate: Date;

	@Metadata({
		header: LocalizedTextIds.CompanyDetailsLeadProductStage,
		indexable: true,
	})
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		dynamicChoiceKey: DynamicChoiceKey.RDStage,
	})
	@Metadata({ indexable: true })
	public currentRdStage: string;

	@Metadata({ header: LocalizedTextIds.Description, indexable: true })
	@Updateable({
		editType: EditItemType.Multiline,
		maxLength: 32000,
		required: true,
	})
	@Metadata({ indexable: true })
	public description: string;

	@Metadata({ header: LocalizedTextIds.BlueKnight, indexable: true })
	public isBlueKnight: boolean;
	@Metadata({ indexable: true })
	public isExcludeFromReporting: boolean;
	@Metadata({ indexable: true })
	public isPublicityAgreementSigned: boolean;
	@Metadata({ indexable: true })
	public isPubliclyOffered: boolean;

	@Metadata({ header: LocalizedTextIds.QFCAwardee, indexable: true })
	public isQfcWinner: boolean;
	@Metadata({ indexable: true })
	public isStealthMode: boolean;
	@Metadata({ indexable: true })
	public locations: Location[];

	@Metadata({ header: LocalizedTextIds.CompanyName, indexable: true })
	@Updateable({
		maxLength: 255,
		showLength: true,
		required: true,
		updateConversion: UpdateConversion.CorrectedWhitespace,
	})
	@Metadata({ indexable: true })
	public name: string;

	@Metadata({ header: LocalizedTextIds.Logo, indexable: true })
	@Updateable({
		editType: EditItemType.Logo,
	})
	@Metadata({ indexable: true })
	public logoBase64: string;
	@Metadata({ indexable: true })
	public logoBase64Hash: string;
	@Metadata({ indexable: true })
	public statuses: string[];
	@Metadata({ indexable: true })
	public modelId: string;

	@Updateable({
		updateConversion: UpdateConversion.DateString,
	})
	@Metadata({ indexable: true })
	public opportunityLastModifiedDate: Date;

	@Metadata({ header: LocalizedTextIds.Indication, indexable: true })
	@Updateable({
		editType: EditItemType.MultiModal,
		updateConversion: UpdateConversion.DelimitedString,
		labelTextId: LocalizedTextIds.IndicationQuestion,
		referenceDataKey: 'indications',
	})
	@Metadata({ indexable: true })
	public primaryIndication: string[];

	@Metadata({ header: LocalizedTextIds.PrimarySector, indexable: true })
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		labelTextId: LocalizedTextIds.PrimarySectorQuestion,
		// The decorator code can't handle the sector delimited string
		// because it needs to handle the remaps, which are not supported
		// by the decorator.
		//	updateConversion: UpdateConversion.DelimitedString,
		referenceDataKey: 'sectors',
		required: true,
	})
	@Metadata({ indexable: true })
	public primarySector: string[];

	@Metadata({ header: LocalizedTextIds.PrimarySubSector, indexable: true })
	@Updateable({
		editType: EditItemType.MultiModal,
		labelTextId: LocalizedTextIds.PrimarySubsectorQuestion,
		dynamicChoiceKey: DynamicChoiceKey.PrimarySubsector,
		updateConversion: UpdateConversion.DelimitedString,
		preconditionProp: 'primarySector',
		referenceDataKey: 'subsectors',
		required: true,
	})
	@Metadata({ indexable: true })
	public primarySubSector: string[];
	@Metadata({ indexable: true })
	public primarySectorDetail: string;

	@Metadata({ header: LocalizedTextIds.Problem, indexable: true })
	@Updateable({
		editType: EditItemType.Multiline,
		maxLength: 32000,
		required: true,
	})
	@Metadata({ indexable: true })
	public problem: string;

	@Metadata({ header: LocalizedTextIds.SecondarySector, indexable: true })
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		labelTextId: LocalizedTextIds.SecondarySectorQuestion,
		referenceDataKey: 'sectors',
	})
	@Metadata({ indexable: true })
	public secondarySector: string;

	@Updateable({
		updateConversion: UpdateConversion.DelimitedString,
	})
	@Metadata({ indexable: true })
	public secondaryIndication: string[];
	@Metadata({ header: LocalizedTextIds.SecondarySubSector, indexable: true })
	@Updateable({
		editType: EditItemType.MultiModal,
		labelTextId: LocalizedTextIds.SecondarySubSectorQuestion,
		dynamicChoiceKey: DynamicChoiceKey.SecondarySubsector,
		updateConversion: UpdateConversion.DelimitedString,
		preconditionProp: 'secondarySector',
		referenceDataKey: 'subsectors',
	})
	@Metadata({ indexable: true })
	public secondarySubSector: string[];
	@Metadata({ indexable: true })
	public secondarySectorDetail: string;

	@Metadata({ header: LocalizedTextIds.Solution, indexable: true })
	@Updateable({
		editType: EditItemType.Multiline,
		maxLength: 32000,
		required: true,
	})
	@Metadata({ indexable: true })
	public solution: string;

	@Metadata({ header: LocalizedTextIds.Tags, indexable: true })
	@Updateable({
		editType: EditItemType.MultiModal,
		dynamicChoiceKey: DynamicChoiceKey.Tags,
		updateConversion: UpdateConversion.DelimitedString,
		subtitleId: LocalizedTextIds.EditItemTaggingIsAGreat,
		promptTextId: LocalizedTextIds.TagsPickerModalBlurbCompanyUpdate,
		delimiter: '_#_',
	})
	@Metadata({ indexable: true })
	public tags: string[];
	@Metadata({ indexable: true })
	public totalSecuredAndContingentAmount: number;
	@Metadata({ indexable: true })
	public wasAcquired: boolean;

	@Metadata({ header: LocalizedTextIds.Website, indexable: true })
	@Updateable({
		maxLength: 255,
		showLength: true,
		updateConversion: UpdateConversion.ValidWebsite,
	})
	@Metadata({ indexable: true })
	public website: string;
	@Metadata({ indexable: true })
	public opportunityOwnerContactId: string;
	@Metadata({ indexable: true })
	public siteHead: Contact;
	@Metadata({ indexable: true })
	public coOwners: CoOwnerContact[];
	@Metadata({ indexable: true })
	public ceoContactId: string;
	@Metadata({ indexable: true })
	public companyContact: Contact;
	@Updateable({
		updateConversion: UpdateConversion.BooleanToYesNo,
	})
	@Metadata({ indexable: true })
	public womenLed: boolean;
	@Updateable({
		updateConversion: UpdateConversion.BooleanToYesNo,
	})
	@Metadata({ indexable: true })
	public minorityLed: boolean;

	@Metadata({
		header: LocalizedTextIds.JnjInformationFirstTimeEntrepreneur,
		indexable: true,
	})
	@Updateable({
		isJnjConfidentialInfo: true,
		editType: EditItemType.ConstrainedValue,
		updateConversion: UpdateConversion.BooleanToYesNo,
		updateHeaderKey: LocalizedTextIds.CommunityAndDiversityTitle,
	})
	@Metadata({ indexable: true })
	public firstTimeEntrepreneur: boolean;

	@Metadata({ header: LocalizedTextIds.Country, indexable: true })
	@Updateable({
		isJnjConfidentialInfo: true,
		editType: EditItemType.ConstrainedValue,
		referenceDataKey: 'countryForDeiReportingList',
		promptTextId: LocalizedTextIds.JnjInformationWhereIsYourCompany,
	})
	@Metadata({ indexable: true })
	public countryForDeiReporting: string;

	@Metadata({
		header: LocalizedTextIds.JnjInformationLeadershipDiversity,
		indexable: true,
	})
	@Updateable({
		isJnjConfidentialInfo: true,
		editType: EditItemType.MultiChoice,
		dynamicChoiceKey: DynamicChoiceKey.LeadershipDiversity,
		applyDeiCountryPrefix: true,
		footnoteId: LocalizedTextIds.JnjInformationAboriginalPeoplesMeans,
		footnoteCountries: ['Canada'],
		promptTextId: LocalizedTextIds.JnjInformationPleaseCheckCLevel,
		tooltipId: LocalizedTextIds.JnjInformationWeAreAsking,
		preconditionProp: 'countryForDeiReporting',
		updateConversion: UpdateConversion.CountryPrefixedDelimitedString,
	})
	@Metadata({ indexable: true })
	public leadershipDiversity: string[];

	@Metadata({
		header: LocalizedTextIds.JnjInformationBoardAdvisor,
		indexable: true,
	})
	@Updateable({
		isJnjConfidentialInfo: true,
		editType: EditItemType.MultiChoice,
		dynamicChoiceKey: DynamicChoiceKey.BoardAdvisorDiversity,
		applyDeiCountryPrefix: true,
		footnoteId: LocalizedTextIds.JnjInformationAboriginalPeoplesMeans,
		footnoteCountries: ['Canada'],
		promptTextId: LocalizedTextIds.JnjInformationPleaseCheckBoard,
		tooltipId: LocalizedTextIds.JnjInformationWeAreAsking,
		preconditionProp: 'countryForDeiReporting',
		updateConversion: UpdateConversion.CountryPrefixedDelimitedString,
	})
	@Metadata({ indexable: true })
	public boardAdvisorDiversity: string;

	@Metadata({
		header: LocalizedTextIds.JnjInformationWomenLedOrg,
		indexable: true,
	})
	@Updateable({
		isJnjConfidentialInfo: true,
		editType: EditItemType.ConstrainedValue,
		promptTextId: LocalizedTextIds.JnjInformationDoYouHaveAnyCLevel,
		updateConversion: UpdateConversion.BooleanToYesNo,
	})
	@Metadata({ indexable: true })
	public womenLedOrgLeadership: string;

	@Metadata({
		header: LocalizedTextIds.JnjInformationWomenLedBoard,
		indexable: true,
	})
	@Updateable({
		isJnjConfidentialInfo: true,
		editType: EditItemType.ConstrainedValue,
		promptTextId: LocalizedTextIds.JnjInformationDoYouHaveAnyBoard,
		updateConversion: UpdateConversion.BooleanToYesNo,
	})
	@Metadata({ indexable: true })
	public womenLedBoardOfDirectorsOrEquiv: boolean;
	@Updateable({
		updateConversion: UpdateConversion.BooleanToYesNo,
	})
	@Metadata({ indexable: true })
	public topTierTeam: boolean;

	@Metadata({ indexable: true })
	public isJPAL?: boolean;

	/** The Public version of the Company may contain a subset of Opportunities for Deal aggregation */
	@Metadata({ indexable: true })
	public opportunities: Opportunity[];
	@Metadata({ indexable: true })
	public limitedDealOpportunities: LimitedDealOpportunity[];

	@Metadata({
		header: LocalizedTextIds.CompanyDetailsKeyDifferentiation,
	})
	@Metadata({ indexable: true })
	public keyDifferentiation: string;

	@Metadata({ header: LocalizedTextIds.KeyManagement, indexable: true })
	@Updateable({
		isJnjConfidentialInfo: true,
		editType: EditItemType.Multiline,
		maxLength: 32768,
		showLength: false,
	})
	@Metadata({ indexable: true })
	public keyMgmtAndAdvBm: string;
	@Metadata({ indexable: true })
	public priority: string;
	@Updateable({
		updateConversion: UpdateConversion.DateString,
	})
	@Metadata({ indexable: true })
	public progressCreatedDate: Date;
	@Metadata({ indexable: true })
	public progressSubject: string;

	@Metadata({
		header: LocalizedTextIds.CompanyDetailsRecentUpdate,
		indexable: true,
	})
	public progressUpdate: string;
	@Metadata({ indexable: true })
	public jpalContactId: string;
	/** 0 or more JPAL contacts, with the 1st being the one referenced by vw_nav_company.jpal_contact_id and the rest sorted alphabetically */
	@Metadata({ indexable: true })
	public jpalContacts: Contact[];
	@Metadata({ indexable: true })
	public deals: Deal[];
	@Metadata({ indexable: true })
	public funding: Funding[];
	@Metadata({ indexable: true })
	public navigatorUrl: string;
	@Metadata({ indexable: true })
	public highestLevelOfFundingSecured = 'Other';

	// BlueKnight/BARDA properties
	/** Referred to as "Alignment" in the UI */
	@BlueKnight()
	@Metadata({
		header: LocalizedTextIds.BlueKnightInformationAlignment,
		indexable: true,
	})
	@Updateable({
		labelTextId: LocalizedTextIds.BlueKnightAlignment1,
		editType: EditItemType.Multiline,
		maxLength: 1000,
		required: true,
	})
	@Metadata({ indexable: true })
	public companyObjective?: string;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.BlueKnightGoal, indexable: true })
	@Updateable({
		labelTextId: LocalizedTextIds.BlueKnightAlignment2,
		editType: EditItemType.MultiChoice,
		referenceDataKey: 'blueKnightGoals',
		updateConversion: UpdateConversion.DelimitedString,
		required: true,
	})
	@Metadata({ indexable: true })
	public alignedGoal?: string[];

	@BlueKnight()
	@Metadata({
		header: LocalizedTextIds.CompanyDetailsApproach,
		indexable: true,
	})
	@Updateable({
		labelTextId: LocalizedTextIds.BlueKnightInformationDescribeThe,
		editType: EditItemType.Multiline,
		maxLength: 1000,
		required: true,
	})
	@Metadata({ indexable: true })
	public approachUsecase?: string;

	@BlueKnight()
	@Metadata({
		header: LocalizedTextIds.CompanyDetailsAnticipated,
		indexable: true,
	})
	@Updateable({
		labelTextId: LocalizedTextIds.BlueKnightInformationCurrentPlans,
		editType: EditItemType.Multiline,
		maxLength: 500,
		required: true,
	})
	@Metadata({ indexable: true })
	public entryExitStrategy?: string;

	@BlueKnight()
	@Metadata({
		header: LocalizedTextIds.CompanyDetailsMentorship,
		indexable: true,
	})
	@Updateable({
		labelTextId: LocalizedTextIds.BlueKnightInformationDescribeMentorship,
		editType: EditItemType.Multiline,
		required: true,
		maxLength: 1000,
	})
	@Metadata({ indexable: true })
	public mentorship?: string;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.CurrentChallenges, indexable: true })
	@Updateable({
		labelTextId: LocalizedTextIds.BlueKnightInformationDescribeChallenges,
		editType: EditItemType.Multiline,
		required: true,
		maxLength: 1000,
	})
	@Metadata({ indexable: true })
	public challenges?: string;

	@BlueKnight()
	@Metadata({
		header: LocalizedTextIds.BlueKnightInformationPriorities,
		indexable: true,
	})
	@Updateable({
		labelTextId: LocalizedTextIds.GoalsAndPrioritiesQuestion,
		editType: EditItemType.Multiline,
		required: true,
		maxLength: 1000,
	})
	@Metadata({ indexable: true })
	public priorities?: string;

	@BlueKnight()
	@Metadata({
		header: LocalizedTextIds.BlueKnightInformationCurrentSize,
		indexable: true,
	})
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		labelTextId: LocalizedTextIds.CurrentTeamSizeQuestion,
		referenceDataKey: 'teamSizeOptions',
		required: true,
	})
	@Metadata({ indexable: true })
	public currentTeamSizeRange?: string;

	@BlueKnight()
	@Metadata({
		header: LocalizedTextIds.BlueKnightInformationCompositionAndGrowthHeader,
	})
	@Updateable({
		labelTextId: LocalizedTextIds.BlueKnightInformationCompositionAndGrowth,
		editType: EditItemType.Multiline,
		maxLength: 500,
	})
	@Metadata({ indexable: true })
	public compositionAndGrowth?: string;

	@BlueKnight()
	@Metadata({
		header: LocalizedTextIds.ConferencesAndEvents,
		indexable: true,
	})
	@Updateable({
		labelTextId: LocalizedTextIds.ConferencesAndEventsQuestion,
		editType: EditItemType.Multiline,
		required: true,
		maxLength: 1000,
	})
	@Metadata({ indexable: true })
	public conferencesAndEvents?: string;

	@BlueKnight()
	@Metadata({
		header: LocalizedTextIds.BlueKnightCommercialPartnerships,
		indexable: true,
	})
	@Updateable({
		editType: EditItemType.Multiline,
		required: true,
		maxLength: 300,
	})
	@Metadata({ indexable: true })
	public commercialPartnerships?: string;

	@BlueKnight()
	@Metadata({
		header: LocalizedTextIds.BlueKnightRDPartnerships,
		indexable: true,
	})
	@Updateable({
		editType: EditItemType.Multiline,
		required: true,
		maxLength: 300,
	})
	@Metadata({ indexable: true })
	public rAndDPartnerships?: string;

	@BlueKnight()
	@Metadata({
		header: LocalizedTextIds.BlueKnightOtherPartnerships,
		indexable: true,
	})
	@Updateable({
		editType: EditItemType.Multiline,
		required: true,
		maxLength: 300,
	})
	@Metadata({ indexable: true })
	public otherPartnerships?: string;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.OverallTRL, indexable: true })
	@Updateable({
		labelTextId: LocalizedTextIds.OverallTRLQuestion,
		editType: EditItemType.ConstrainedValue,
		referenceDataKey: 'technologyReadinessLevelList',
		required: true,
	})
	@Metadata({ indexable: true })
	public overallTRL?: string;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.AlignedTRL, indexable: true })
	@Updateable({
		labelTextId: LocalizedTextIds.AlignedTRLQuestion,
		editType: EditItemType.ConstrainedValue,
		referenceDataKey: 'technologyReadinessLevelList',
		required: true,
	})
	@Metadata({ indexable: true })
	public alignedTRL?: string;

	@BlueKnight()
	@Metadata({
		header: LocalizedTextIds.CompositionOfMatter,
	})
	@Updateable({
		labelTextId: LocalizedTextIds.CompositionOfMatterQuestion,
		editType: EditItemType.ConstrainedValue,
		required: true,
		updateConversion: UpdateConversion.BooleanToYesNo,
	})
	@Metadata({ indexable: true })
	public compositionOfMatter?: boolean;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.SecuredIP, indexable: true })
	@Updateable({
		labelTextId: LocalizedTextIds.SecuredIPQuestion,
		editType: EditItemType.ConstrainedValue,
		required: true,
		updateConversion: UpdateConversion.BooleanToYesNo,
	})
	@Metadata({ indexable: true })
	public securedIP?: boolean;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.FiledIP, indexable: true })
	@Updateable({
		labelTextId: LocalizedTextIds.FiledIPQuestion,
		editType: EditItemType.ConstrainedValue,
		required: true,
		updateConversion: UpdateConversion.BooleanToYesNo,
	})
	@Metadata({ indexable: true })
	public filedIP?: boolean;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.AdditionalIPDetails, indexable: true })
	@Updateable({
		labelTextId: LocalizedTextIds.AdditionalIPDetailsQuestion,
		editType: EditItemType.Multiline,
		maxLength: 500,
	})
	@Metadata({ indexable: true })
	public addtlIPDetails?: string;
	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.FundingStage, indexable: true })
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		labelTextId: LocalizedTextIds.FundingStageQuestion,
		referenceDataKey: 'blueKnightFundingRounds',
		required: true,
	})
	@Metadata({ indexable: true })
	public fundingStage?: string;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.FundingStageDetails, indexable: true })
	@Updateable({
		labelTextId: LocalizedTextIds.PleaseClarify,
		editType: EditItemType.Multiline,
		preconditionProp: 'fundingStage',
		maxLength: 500,
	})
	@Metadata({ indexable: true })
	public fundingStageDetails?: string;

	@BlueKnight()
	@Metadata({
		header: LocalizedTextIds.NonDilutiveFunding,
	})
	@Updateable({
		labelTextId: LocalizedTextIds.NonDilutiveFundingQuestion,
		editType: EditItemType.Currency,
		required: true,
		maxLength: 100,
	})
	@Metadata({ indexable: true })
	public nonDilutiveFunding?: number;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.DilutiveFunding, indexable: true })
	@Updateable({
		labelTextId: LocalizedTextIds.DilutiveFundingQuestion,
		editType: EditItemType.Currency,
		required: true,
		maxLength: 100,
	})
	@Metadata({ indexable: true })
	public dilutiveFunding?: number;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.MajorMilestones, indexable: true })
	@Updateable({
		labelTextId: LocalizedTextIds.MajorMilestonesQuestion,
		editType: EditItemType.Multiline,
		required: true,
		maxLength: 1400,
	})
	@Metadata({ indexable: true })
	public majorMilestones?: string;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.CurrentStagePharma, indexable: true })
	@Updateable({
		labelTextId: LocalizedTextIds.PharmaStageQuestion,
		editType: EditItemType.ConstrainedValue,
		referenceDataKey: 'blueKnightPharmaStage',
		required: true,
	})
	@Metadata({ indexable: true })
	public currentPharmaStage?: string;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.CurrentStageMedTech, indexable: true })
	@Updateable({
		labelTextId: LocalizedTextIds.MedTechStageQuestion,
		editType: EditItemType.ConstrainedValue,
		referenceDataKey: 'blueKnightMedTechStage',
		required: true,
	})
	@Metadata({ indexable: true })
	public currentMedTechStage?: string;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.AdditionalContext, indexable: true })
	@Updateable({
		labelTextId: LocalizedTextIds.AdditionalContextQuestion,
		editType: EditItemType.Multiline,
		maxLength: 800,
	})
	@Metadata({ indexable: true })
	public rndStageDetails?: string;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.RegStatus, indexable: true })
	@Updateable({
		labelTextId: LocalizedTextIds.RegulatoryApprovalQuestion,
		editType: EditItemType.ConstrainedValue,
		referenceDataKey: 'regulatoryStages',
		required: true,
	})
	@Metadata({ indexable: true })
	public regulatoryStatus?: string;

	@BlueKnight()
	@Metadata({
		header: LocalizedTextIds.AnticipatedCommercialProductYr,
		indexable: true,
	})
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		referenceDataKey: 'years',
		labelTextId: LocalizedTextIds.AnticipatedCommercialProductYrQuestion,
		required: true,
	})
	@Metadata({ indexable: true })
	public anticipatedCommercialProductYr?: number;

	@BlueKnight()
	@Metadata({ header: LocalizedTextIds.NonLeadRegStatus, indexable: true })
	@Updateable({
		editType: EditItemType.Multiline,
		labelTextId: LocalizedTextIds.NonLeadRegStatusQuestion,
		maxLength: 500,
	})
	@Metadata({ indexable: true })
	public nonLeadRegStatus?: string;

	@Metadata({ header: LocalizedTextIds.Assets, indexable: true })
	public assets?: Asset[];
}

export function isBlueKnightProperty(name: CompanyKey): boolean {
	return getBlueKnightFields().includes(name);
}

export function getBlueKnightFields(): CompanyKey[] {
	return getBlueKnightFieldsRaw(Company.prototype);
}

export function getUpdateableCompanyFields(): CompanyKey[] {
	return getUpdateableFieldsRaw(Company.prototype);
}

export function getCompanyMetadata(propertyKey: CompanyKey): Metadata {
	// companyContactTitle and contactName arent "real" properties, so they need
	// to have metadata defined here
	if (propertyKey === 'companyContactTitle') {
		return {
			header: LocalizedTextIds.ContactTitle,
			indexable: true,
		};
	}

	if (propertyKey === 'contactName') {
		return {
			header: LocalizedTextIds.CompanyContact,
			indexable: true,
		};
	}

	return getMetadataRaw(propertyKey, Company.prototype) ?? {};
}

export function getCompanyUpdateOptions(
	propertyKey: CompanyKey
): UpdateOptions {
	if (propertyKey === 'companyContactTitle') {
		return {
			maxLength: 255,
			showLength: true,
		};
	}

	if (propertyKey === 'contactName') {
		return {
			isReadOnly: true,
			subtitleId: LocalizedTextIds.PublicInformationReachOut,
		};
	}

	return getUpdateOptionsRaw(propertyKey, Company.prototype) ?? {};
}

export function getKeysByUpdateConversion(
	conversion: UpdateConversion
): CompanyKey[] {
	return getUpdateableCompanyFields().filter(
		(field) =>
			getCompanyUpdateOptions(field)?.updateConversion === conversion
	);
}

export function getDependentCompanyFields(key: CompanyKey): CompanyKey[] {
	return (
		getUpdateableCompanyFields().filter(
			(field) => getCompanyUpdateOptions(field)?.preconditionProp === key
		) ?? []
	);
}

export function getCompanyFieldsWithConversions(): CompanyKey[] {
	return getUpdateableCompanyFields().filter(
		(field) => getCompanyUpdateOptions(field)?.updateConversion != undefined
	);
}

export function getMultiModalFields(): CompanyKey[] {
	return getUpdateableCompanyFields().filter(
		(field) =>
			getCompanyUpdateOptions(field)?.editType === EditItemType.MultiModal
	);
}
